import { Box, Button, Stack, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

interface Props {
  /** Optional reset handler (e.g. from an error boundary). Defaults to a full reload. */
  onRetry?: () => void;
}

/**
 * Friendly 500 page (Phase 11). Shown by the app's ErrorBoundary when a render/runtime error occurs,
 * so users never see a raw server/stack error. Uses no router hooks so it is safe to render even when
 * the failure happened outside the router tree.
 */
export default function ServerError({ onRetry }: Props) {
  const retry = onRetry ?? (() => window.location.reload());
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh', p: 2 }}>
      <Stack alignItems="center" spacing={1.5} textAlign="center" sx={{ width: '100%', maxWidth: 440 }}>
        <ErrorOutline sx={{ fontSize: { xs: 48, sm: 56 }, color: 'error.main' }} />
        <Typography variant="h5" component="h1">Something went wrong</Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={420}>
          An unexpected error occurred. Please try again — if the problem continues, contact support.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="contained" onClick={retry}>
            Try again
          </Button>
          <Button variant="outlined" onClick={() => window.location.assign('/dashboard')}>
            Go home
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
